import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import { API_URL } from "../../utils/constant";

const RedTeamingAttackTypes = () => {
  const [attackTypes, setAttackTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAttackTypes();
  }, []);

  const fetchAttackTypes = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/red-teaming/attack-types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch attack types');
      }

      const data = await response.json();
      setAttackTypes(data);
    } catch (error) {
      // console.error removed
      toastr.error('Error', 'Failed to load attack types');
      
      // Fallback to mock data
      setAttackTypes([
        {
          id: "directory_traversal",
          name: "Directory Traversal",
          description: "Test for path traversal vulnerabilities using real payloads",
          category: "Web Application",
          severity: "High",
          tools: ["curl", "wget"],
          payloads: ["../../../etc/passwd", "..\\..\\..\\windows\\system32\\drivers\\etc\\hosts"]
        },
        {
          id: "command_injection",
          name: "Command Injection",
          description: "Test for command injection vulnerabilities using curl-based testing",
          category: "Web Application",
          severity: "Critical",
          tools: ["curl", "nc"],
          payloads: ["; ls -la", "| whoami", "&& cat /etc/passwd"]
        },
        {
          id: "ssrf",
          name: "SSRF",
          description: "Test for Server-Side Request Forgery vulnerabilities",
          category: "Web Application",
          severity: "High",
          tools: ["curl", "burp"],
          payloads: ["http://localhost", "http://127.0.0.1", "http://169.254.169.254"]
        },
        {
          id: "xxe",
          name: "XXE",
          description: "Test for XML External Entity injection vulnerabilities",
          category: "Web Application",
          severity: "High",
          tools: ["curl", "xmlstarlet"],
          payloads: ["<!DOCTYPE test [<!ENTITY xxe SYSTEM 'file:///etc/passwd'>]>"]
        },
        {
          id: "deserialization",
          name: "Deserialization",
          description: "Test for insecure deserialization vulnerabilities",
          category: "Web Application",
          severity: "Critical",
          tools: ["ysoserial", "phpggc"],
          payloads: ["Java serialized objects", "PHP serialized objects"]
        },
        {
          id: "weak_jwt",
          name: "Weak JWT",
          description: "Test for weak JWT implementations and vulnerabilities",
          category: "Authentication",
          severity: "Medium",
          tools: ["jwt_tool", "jwt.io"],
          payloads: ["None algorithm", "Weak secret", "No expiration"]
        },
        {
          id: "oauth_bypass",
          name: "OAuth Bypass",
          description: "Test for OAuth flow vulnerabilities and bypasses",
          category: "Authentication",
          severity: "High",
          tools: ["burp", "oauth2"],
          payloads: ["State parameter bypass", "Redirect URI manipulation"]
        },
        {
          id: "rate_limit_bypass",
          name: "API Rate Limit Bypass",
          description: "Test for API rate limiting bypasses",
          category: "API Security",
          severity: "Medium",
          tools: ["curl", "python"],
          payloads: ["Header manipulation", "IP rotation", "Timing attacks"]
        },
        {
          id: "business_logic",
          name: "Business Logic Flaws",
          description: "Test for business logic vulnerabilities",
          category: "Application Logic",
          severity: "High",
          tools: ["burp", "manual"],
          payloads: ["Price manipulation", "Access control bypass", "Race conditions"]
        },
        {
          id: "file_upload",
          name: "File Upload Vulnerabilities",
          description: "Test for insecure file upload implementations",
          category: "Web Application",
          severity: "High",
          tools: ["curl", "burp"],
          payloads: ["PHP shells", "Executable files", "Path traversal in filename"]
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="page-title-box">
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/red-teaming">Red Teaming</Link>
                </li>
                <li className="breadcrumb-item active">Attack Types</li>
              </ol>
            </div>
            <h4 className="page-title">Red Teaming Attack Types</h4>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="header-title mb-3">Available Attack Types</h4>
              <p className="text-muted">
                These are the high-impact attack types available for Red Teaming simulations. 
                Each attack type uses real security tools and payloads to test your infrastructure.
              </p>

              <div className="row">
                {attackTypes.map((attackType) => (
                  <div key={attackType.id} className="col-md-6 col-lg-4 mb-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h5 className="card-title mb-0">{attackType.name}</h5>
                          <span className={`badge badge-${getSeverityColor(attackType.severity)}`}>
                            {attackType.severity}
                          </span>
                        </div>
                        <p className="card-text text-muted">{attackType.description}</p>
                        
                        <div className="mb-2">
                          <small className="text-muted">
                            <strong>Category:</strong> {attackType.category}
                          </small>
                        </div>
                        
                        <div className="mb-2">
                          <small className="text-muted">
                            <strong>Tools:</strong> {attackType.tools.join(', ')}
                          </small>
                        </div>
                        
                        <div className="mb-3">
                          <small className="text-muted">
                            <strong>Sample Payloads:</strong>
                          </small>
                          <div className="mt-1">
                            {attackType.payloads.map((payload, index) => (
                              <code key={index} className="d-block small text-muted mb-1">
                                {payload}
                              </code>
                            ))}
                          </div>
                        </div>
                        
                        <button className="btn btn-sm btn-primary">
                          <i className="fa fa-play mr-1"></i>
                          Use in Simulation
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="header-title mb-3">Attack Type Categories</h4>
              <div className="row">
                <div className="col-md-3">
                  <div className="text-center">
                    <i className="fa fa-globe fa-2x text-primary mb-2"></i>
                    <h5>Web Application</h5>
                    <p className="text-muted">Directory traversal, command injection, SSRF, XXE</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center">
                    <i className="fa fa-key fa-2x text-warning mb-2"></i>
                    <h5>Authentication</h5>
                    <p className="text-muted">Weak JWT, OAuth bypass, session management</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center">
                    <i className="fa fa-code fa-2x text-info mb-2"></i>
                    <h5>API Security</h5>
                    <p className="text-muted">Rate limit bypass, API abuse, parameter pollution</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center">
                    <i className="fa fa-cogs fa-2x text-success mb-2"></i>
                    <h5>Business Logic</h5>
                    <p className="text-muted">Logic flaws, access control, race conditions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedTeamingAttackTypes; 